/**
SELLERIE MARINE - SERVICE COMMANDES FOURNISSEUR GROUPÉES

📋 DESCRIPTION:
Service spécialisé dans la génération de commandes fournisseurs groupées.
Regroupe matières et fournitures de plusieurs devis avec exclusion stock.

🎯 RESPONSABILITÉS:
- Regroupement intelligent matières/fournitures multi-devis
- Exclusion automatique matières marquées "En stock"
- Calcul adaptatif fermetures avec service spécialisé
- Gestion mousses avec dimensions et classification épaisseur
- Génération commandes avec numérotation unique
- Export formaté pour transmission fournisseurs
- Traçabilité complète devis d'origine par article

🔗 DÉPENDANCES:
- MatiereService: Référentiel matières et prix
- ParametreService: Fournitures et accessoires
- FermetureService: Calculs adaptatifs fermetures
- types.ts: Interface Devis pour données source

📡 INTERACTIONS:
- Services: Récupération données référentiels
- Devis: Extraction matières/fournitures articles
- Fermetures: Calculs adaptatifs longueurs/prix
- Export: Formatage commandes pour fournisseurs
- Historique: Traçabilité commandes générées

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Exclusion intelligente matières en stock (calepinage + materiaux)
- Service fermetures adaptatives avec fallback
- Regroupement par clé unique (matière+couleur, fourniture+dimensions)
- Calculs précis volumes mousses avec dimensions détaillées
- Numérotation commandes unique avec timestamp
- Export formaté prêt transmission fournisseurs
- Statistiques détaillées exclusions/inclusions
*/

import { Devis } from './types';
import { matiereService } from './MatiereService';
import { parametreService, Fourniture } from './ParametreService';
import { fermetureService } from './FermetureService';
import { mousseService } from './MousseService';
import { Matiere } from '../pages/Devis/ConfigurateurArticle/types';
import { FallbackAlerts } from './FallbackNotificationService';

// Types pour la commande groupée
export interface MatiereGroupee {
  matiereId: string;
  nom: string;
  type: string;
  couleur: string;
  quantiteMetres: number;
  prixUnitaire: number;
  montantTotal: number;
  devisOrigines: Array<{
    devisId: string;
    reference: string;
    quantiteUtilisee: number;
  }>;
}

export interface FournitureGroupee {
  fournitureId: string;
  nom: string;
  type: string;
  quantite: number;
  unite: string;
  prixUnitaire: number;
  montantTotal: number;
  dimensions?: {
    longueur: number;
    largeur: number;
    hauteur: number;
    volume: number;
  };
  descriptionDetaille?: string; // "50×30×10 cm (0.015 m³)"
  devisOrigines: Array<{
    devisId: string;
    reference: string;
    quantiteUtilisee: number;
  }>;
}

export interface CommandeGroupee {
  numeroCommande: string;
  dateCreation: string;
  devisInclus: string[];
  matieres: MatiereGroupee[];
  fournitures: FournitureGroupee[];
  totaux: {
    montantMatieres: number;
    montantFournitures: number;
    montantTotal: number;
    nombreDevis: number;
  };
  statistiques: {
    matieresExcluesStock: number;
    matieresACommander: number;
  };
}

class CommandeFournisseurService {
  /**
   * Calcule le prix fournisseur à partir du prix public TTC et de la remise fournisseur
   */
  private calculerPrixFournisseur(prixPublicTTC: number, remiseFournisseur: number = 0): number {
    // 1. Convertir TTC → HT (les prix matières sont saisis en TTC)
    const prixPublicHT = prixPublicTTC / 1.2;
    
    // 2. Appliquer la remise fournisseur
    const prixFournisseurHT = prixPublicHT * (1 - remiseFournisseur / 100);
    
    return prixFournisseurHT;
  }

  /**
   * Génère un numéro de commande unique
   */
  private genererNumeroCommande(): string {
    const date = new Date();
    const annee = date.getFullYear();
    const timestamp = Date.now().toString().slice(-6); // 6 derniers chiffres
    return `CMD-${annee}-${timestamp}`;
  }

  /**
   * Vérifie si une matière est en stock pour un article donné
   */
  private estMatiereEnStock(article: any, matiereId: string): boolean {
    // Vérifier dans calepinage.matieresEnStock
    const calepinage = article.prixCalcule?.details?.calepinage;
    if (calepinage?.matieresEnStock) {
      // Chercher la matière dans les différentes faces
      const materiaux = article.materiaux;
      if (materiaux) {
        if (materiaux.dessus?.matiere === matiereId && calepinage.matieresEnStock.dessus) return true;
        if (materiaux.dessous?.matiere === matiereId && calepinage.matieresEnStock.dessous) return true;
        if (materiaux.cotes?.matiere === matiereId && calepinage.matieresEnStock.cotes) return true;
      }
    }

    // Vérifier dans materiaux.*.enStock
    const materiaux = article.materiaux;
    if (materiaux) {
      if (materiaux.dessus?.matiere === matiereId && materiaux.dessus?.enStock) return true;
      if (materiaux.dessous?.matiere === matiereId && materiaux.dessous?.enStock) return true;
      if (materiaux.cotes?.matiere === matiereId && materiaux.cotes?.enStock) return true;
    }

    return false;
  }

  /**
   * Extrait et regroupe les matières de plusieurs devis (EXCLUT celles en stock)
   * ✅ CORRECTION : Utilise devisOptimise.groupesMatiere (données déjà regroupées par devis)
   * au lieu de article.prixCalcule (données par article qui causent la duplication)
   */
  private async extraireMatieresGroupees(devisList: Devis[]): Promise<{ matieres: MatiereGroupee[], statistiques: { excluesStock: number, aCommander: number } }> {
    const matieresMap = new Map<string, MatiereGroupee>();
    const matieresRef = await matiereService.getMatieres();
    const matieresRefMap = new Map<string, Matiere>();
    
    let matieresExcluesStock = 0;
    let matieresACommander = 0;
    
    // Créer un map des matières de référence
    matieresRef.forEach(matiere => {
      matieresRefMap.set(matiere.id, matiere);
    });

    console.log('🔍 [EXTRACTION] Début extraction matières pour', devisList.length, 'devis');

    // Parcourir tous les devis
    devisList.forEach(devis => {
      console.log(`🔍 [EXTRACTION] Traitement devis ${devis.reference}`);
      
      // ✅ CORRECTION : Utiliser devisOptimise qui contient les matières déjà regroupées
      const devisOptimise = (devis as any).devisOptimise;
      
      if (!devisOptimise || !devisOptimise.groupesMatiere) {
        console.warn(`⚠️ [EXTRACTION] Pas de devisOptimise pour ${devis.reference}, fallback sur prixCalcule`);
        
        // Fallback : utiliser prixCalcule (ancien comportement)
        // MAIS en s'assurant de ne traiter qu'une seule fois par matière
        const matieresVues = new Set<string>();
        
        devis.articles.forEach(article => {
          const calepinage = article.prixCalcule?.details?.calepinage;
          
          if (calepinage?.matieres) {
            calepinage.matieres.forEach((matiereUtilisee: any) => {
              const cleMatiere = `${matiereUtilisee.id}-${matiereUtilisee.couleur}`;
              
              // Ne traiter qu'une seule fois par matière dans ce devis
              if (matieresVues.has(cleMatiere)) {
                console.log(`  ⏭️ Matière ${cleMatiere} déjà traitée pour ce devis, skip`);
                return;
              }
              matieresVues.add(cleMatiere);
              
              const matiereRef = matieresRefMap.get(matiereUtilisee.id);
              if (!matiereRef || !matiereUtilisee.longueurMetres) return;

              // VÉRIFIER SI LA MATIÈRE EST EN STOCK
              if (this.estMatiereEnStock(article, matiereUtilisee.id)) {
                matieresExcluesStock++;
                return;
              }

              matieresACommander++;
              const cle = `${matiereUtilisee.id}-${matiereUtilisee.couleur}`;
              
              console.log(`  ✅ Matière ${matiereRef.nom} ${matiereUtilisee.couleur}: ${matiereUtilisee.longueurMetres}m`);
              
              if (matieresMap.has(cle)) {
                const matiereGroupee = matieresMap.get(cle)!;
                matiereGroupee.quantiteMetres += matiereUtilisee.longueurMetres;
                matiereGroupee.montantTotal = matiereGroupee.quantiteMetres * matiereGroupee.prixUnitaire;
                matiereGroupee.devisOrigines.push({
                  devisId: devis.id || '',
                  reference: devis.reference,
                  quantiteUtilisee: matiereUtilisee.longueurMetres
                });
              } else {
                // ✅ CORRECTION : Calculer le prix fournisseur (fallback aussi)
                const prixFournisseur = this.calculerPrixFournisseur(
                  matiereRef.prix,
                  (matiereRef as any).remiseFournisseur || 50
                );
                
                matieresMap.set(cle, {
                  matiereId: matiereUtilisee.id,
                  nom: `${matiereRef.nom} ${matiereUtilisee.couleur}`,
                  type: matiereRef.type,
                  couleur: matiereUtilisee.couleur,
                  quantiteMetres: matiereUtilisee.longueurMetres,
                  prixUnitaire: prixFournisseur,
                  montantTotal: matiereUtilisee.longueurMetres * prixFournisseur,
                  devisOrigines: [{
                    devisId: devis.id || '',
                    reference: devis.reference,
                    quantiteUtilisee: matiereUtilisee.longueurMetres
                  }]
                });
              }
            });
          }
        });
        
        return; // Fin du traitement pour ce devis (fallback)
      }
      
      // ✅ NOUVEAU : Utiliser groupesMatiere (données déjà regroupées par devis)
      const groupes = devisOptimise.groupesMatiere;
      console.log(`  📦 ${groupes.length} groupe(s) de matières trouvé(s)`);
      
      groupes.forEach((groupe: any) => {
        // ✅ CORRECTION : Gérer les 2 structures (estimation ET optimisation)
        // Mode estimation: { matiere, couleur, longueurTotale: 1650 (cm) }
        // Mode optimisé: { matiere, couleur, calepinage: { longueurTotale: 162 (cm) } }
        
        if (!groupe.matiere) {
          console.warn(`  ⚠️ Groupe sans matiere:`, groupe);
          return;
        }
        
        // Récupérer longueurTotale selon le mode
        let longueurTotaleCm = 0;
        if (groupe.longueurTotale) {
          // Mode estimation
          longueurTotaleCm = groupe.longueurTotale;
          console.log(`  📏 Mode estimation détecté`);
        } else if (groupe.calepinage?.longueurTotale) {
          // Mode optimisé
          longueurTotaleCm = groupe.calepinage.longueurTotale;
          console.log(`  🎯 Mode optimisé détecté`);
        } else {
          console.warn(`  ⚠️ Aucune longueurTotale trouvée:`, groupe);
          return;
        }
        
        const matiereRef = matieresRefMap.get(groupe.matiere);
        if (!matiereRef) {
          console.warn(`  ⚠️ Matière ${groupe.matiere} non trouvée dans référentiel`);
          return;
        }

        // Convertir longueurTotale de cm en mètres
        const longueurMetres = longueurTotaleCm / 100;

        // Note: Pour devisOptimise, la vérification "en stock" est plus complexe
        // car les données sont déjà regroupées. Pour l'instant on inclut tout.
        matieresACommander++;

        // Clé unique : matiereId + couleur
        const cle = `${groupe.matiere}-${groupe.couleur}`;
        
        console.log(`  ✅ Matière ${matiereRef.nom} ${groupe.couleur}: ${longueurMetres.toFixed(2)}m (${groupe.longueurTotale}cm)`);
        
        if (matieresMap.has(cle)) {
          // Ajouter à la matière existante (accumulation multi-devis)
          const matiereGroupee = matieresMap.get(cle)!;
          matiereGroupee.quantiteMetres += longueurMetres;
          matiereGroupee.montantTotal = matiereGroupee.quantiteMetres * matiereGroupee.prixUnitaire;
          matiereGroupee.devisOrigines.push({
            devisId: devis.id || '',
            reference: devis.reference,
            quantiteUtilisee: longueurMetres
          });
        } else {
          // ✅ CORRECTION : Calculer le prix fournisseur (avec remise)
          const prixFournisseur = this.calculerPrixFournisseur(
            matiereRef.prix,
            (matiereRef as any).remiseFournisseur || 50
          );
          
          // Créer une nouvelle entrée
          matieresMap.set(cle, {
            matiereId: groupe.matiere,
            nom: `${matiereRef.nom} ${groupe.couleur}`,
            type: matiereRef.type,
            couleur: groupe.couleur,
            quantiteMetres: longueurMetres,
            prixUnitaire: prixFournisseur,
            montantTotal: longueurMetres * prixFournisseur,
            devisOrigines: [{
              devisId: devis.id || '',
              reference: devis.reference,
              quantiteUtilisee: longueurMetres
            }]
          });
        }
      });
    });

    console.log(`🔍 [EXTRACTION] Total matières regroupées: ${matieresMap.size}`);
    console.log(`🔍 [EXTRACTION] Détail:`, Array.from(matieresMap.values()).map(m => ({
      nom: m.nom,
      quantite: m.quantiteMetres,
      origines: m.devisOrigines.length
    })));

    return {
      matieres: Array.from(matieresMap.values()).sort((a, b) => a.nom.localeCompare(b.nom)),
      statistiques: {
        excluesStock: matieresExcluesStock,
        aCommander: matieresACommander
      }
    };
  }

  /**
   * Extrait et regroupe les fournitures de plusieurs devis
   */
  private async extraireFournituresGroupees(devisList: Devis[]): Promise<FournitureGroupee[]> {
    const fournituresMap = new Map<string, FournitureGroupee>();
    const fournituresRef = await parametreService.getFournitures();
    const fournituresRefMap = new Map<string, Fourniture>();
    
    // Créer un map des fournitures de référence
    fournituresRef.forEach(fourniture => {
      if (fourniture.id) {
        fournituresRefMap.set(fourniture.nom.toLowerCase(), fourniture);
      }
    });

    // Parcourir tous les devis
    for (let i = 0; i < devisList.length; i++) {
      const devis = devisList[i];
      for (let j = 0; j < devis.articles.length; j++) {
        const article = devis.articles[j];
        const accessoires = article.prixCalcule?.details?.accessoires;
        const options = article.options || {};
        
        if (accessoires) {
          // Traiter les fermetures avec le système adaptatif
          if ((accessoires.fermeture && accessoires.fermeture !== 'aucune') || 
              (options.fermeture && options.fermeture !== 'aucune')) {
            
            try {
              // Utiliser le service de fermetures adaptatives
              const typeFermeture = (options.fermeture || accessoires.fermeture) as 'glissiere' | 'velcro';
              
              if (typeFermeture === 'glissiere' || typeFermeture === 'velcro') {
                const fermetureAdaptative = await fermetureService.calculerFermetureAdaptative({
                  forme: article.forme || 'rectangulaire_bandeau',
                  dimensions: {
                    longueur: article.dimensions?.longueur,
                    largeur: article.dimensions?.largeur,
                    diametre: article.dimensions?.diametre,
                    hauteur: article.dimensions?.hauteur
                  },
                  typeFermeture
                });

                if (fermetureAdaptative) {
                  // Utiliser les données réelles de la fermeture adaptative
                  const nomFermeture = fermetureService.formaterAffichageFermeture(fermetureAdaptative);
                  
                  // ✅ CORRECTION : Calculer prix fournisseur pour fermeture
                  const prixFournisseur = this.calculerPrixFournisseur(
                    fermetureAdaptative.prixUnitaire,
                    fermetureAdaptative.fournitureSelectionnee.marge || 50
                  );
                  
                  this.ajouterFournitureAdaptative(
                    fournituresMap,
                    fermetureAdaptative.fournitureSelectionnee,
                    nomFermeture,
                    article.quantite || 1, // Quantité réelle de l'article
                    'unité',
                    prixFournisseur,
                    devis,
                    article.quantite || 1,
                    fermetureAdaptative.longueurCalculee,
                    fermetureAdaptative.longueurFourniture
                  );
                } else {
                  // Fallback sur l'ancien système
                  this.ajouterFourniture(
                    fournituresMap,
                    fournituresRefMap,
                    'fermeture à glissière',
                    1,
                    'unité',
                    8.50,
                    devis,
                    article.quantite || 1
                  );
                }
              }
            } catch (error) {
              console.warn('Erreur service fermetures adaptatives, utilisation fallback:', error);
              
              // 🚨 NOTIFICATION FALLBACK - Service fermetures
              FallbackAlerts.fermetures(8.5, 'Service fermetures adaptatives');
              // Fallback sur l'ancien système
              this.ajouterFourniture(
                fournituresMap,
                fournituresRefMap,
                'fermeture à glissière',
                1,
                'unité',
                8.50,
                devis,
                article.quantite || 1
              );
            }
          }

          // Traiter les mousses avec dimensions et classement par épaisseur
          if (accessoires.mousse && accessoires.mousse !== 'aucune' && accessoires.volumeMousse) {
            // ✅ CORRECTION : Utiliser options.mousse (ID) au lieu de accessoires.mousse (nom)
            const mousseId = options.mousse || accessoires.mousse;
            
            // 🔍 LOGS DEBUG
            console.log('🔍 [MOUSSE] Article:', article.nom);
            console.log('🔍 [MOUSSE] mousseId depuis options:', mousseId);
            console.log('🔍 [MOUSSE] options.mousse:', options.mousse);
            console.log('🔍 [MOUSSE] accessoires.mousse:', accessoires.mousse);
            
            try {
              // 🔍 Récupérer la vraie mousse depuis la base de données
              const mousseData = await mousseService.getMousseById(mousseId);
              
              console.log('🔍 [MOUSSE] mousseData récupérée:', mousseData);
              
              if (mousseData) {
                // ✅ Utiliser les vraies données de la mousse
                const dimensions = article.dimensions || {};
                
                // Récupérer les dimensions physiques
                const longueur = dimensions.longueur || 0;
                const largeur = dimensions.largeur || 0;
                const hauteur = dimensions.hauteur || 0;
                
                // Créer une clé unique par mousse + épaisseur + dimensions
                const epaisseur = hauteur; // L'épaisseur correspond à la hauteur
                const dimensionsStr = `${longueur}×${largeur}×${hauteur} cm`;
                const nomMousseDetaille = `${mousseData.nom} - épaisseur ${epaisseur} cm`;
                
                // Calculer le prix d'achat fournisseur (sans marge)
                const prixAchatM3 = mousseService.calculerPrixCommande(mousseData);
                
                this.ajouterFournitureMousse(
                  fournituresMap,
                  fournituresRefMap,
                  nomMousseDetaille,
                  accessoires.volumeMousse * (article.quantite || 1),
                  'm³',
                  prixAchatM3,
                  devis,
                  accessoires.volumeMousse,
                  {
                    longueur,
                    largeur,
                    hauteur,
                    volume: accessoires.volumeMousse
                  },
                  dimensionsStr
                );
              } else {
                // Fallback si la mousse n'est pas trouvée
                console.warn(`⚠️ Mousse ${mousseId} non trouvée, utilisation du fallback`);
                const typeMousse = 'standard';
                const dimensions = article.dimensions || {};
                const longueur = dimensions.longueur || 0;
                const largeur = dimensions.largeur || 0;
                const hauteur = dimensions.hauteur || 0;
                const epaisseur = hauteur;
                const dimensionsStr = `${longueur}×${largeur}×${hauteur} cm`;
                const nomMousseDetaille = `mousse ${typeMousse} fallback - épaisseur ${epaisseur} cm`;
                
                this.ajouterFournitureMousse(
                  fournituresMap,
                  fournituresRefMap,
                  nomMousseDetaille,
                  accessoires.volumeMousse * (article.quantite || 1),
                  'm³',
                  this.getPrixMousse(typeMousse),
                  devis,
                  accessoires.volumeMousse,
                  {
                    longueur,
                    largeur,
                    hauteur,
                    volume: accessoires.volumeMousse
                  },
                  dimensionsStr
                );
              }
            } catch (error) {
              console.error('Erreur lors de la récupération de la mousse:', error);
              // Fallback en cas d'erreur
              const typeMousse = 'standard';
              const dimensions = article.dimensions || {};
              const longueur = dimensions.longueur || 0;
              const largeur = dimensions.largeur || 0;
              const hauteur = dimensions.hauteur || 0;
              const epaisseur = hauteur;
              const dimensionsStr = `${longueur}×${largeur}×${hauteur} cm`;
              const nomMousseDetaille = `mousse ${typeMousse} fallback - épaisseur ${epaisseur} cm`;
              
              this.ajouterFournitureMousse(
                fournituresMap,
                fournituresRefMap,
                nomMousseDetaille,
                accessoires.volumeMousse * (article.quantite || 1),
                'm³',
                this.getPrixMousse(typeMousse),
                devis,
                accessoires.volumeMousse,
                {
                  longueur,
                  largeur,
                  hauteur,
                  volume: accessoires.volumeMousse
                },
                dimensionsStr
              );
            }
          }
        }
      }
    }

    return Array.from(fournituresMap.values()).sort((a, b) => a.nom.localeCompare(b.nom));
  }

  /**
   * Ajoute une fourniture adaptative au regroupement (pour les fermetures)
   */
  private ajouterFournitureAdaptative(
    fournituresMap: Map<string, FournitureGroupee>,
    fournitureRef: Fourniture,
    nomFourniture: string,
    quantite: number,
    unite: string,
    prix: number,
    devis: Devis,
    quantiteOriginale: number,
    longueurCalculee: number,
    longueurFourniture: number
  ) {
    // Créer une clé unique basée sur la fourniture réelle sélectionnée
    const cle = `${fournitureRef.nom}-${longueurFourniture}cm`;
    
    if (fournituresMap.has(cle)) {
      // Ajouter à la fourniture existante
      const fournitureGroupee = fournituresMap.get(cle)!;
      fournitureGroupee.quantite += quantite;
      fournitureGroupee.montantTotal = fournitureGroupee.quantite * fournitureGroupee.prixUnitaire;
      fournitureGroupee.devisOrigines.push({
        devisId: devis.id || '',
        reference: devis.reference,
        quantiteUtilisee: quantiteOriginale
      });
    } else {
      // Créer une nouvelle entrée avec les détails adaptatifs
      fournituresMap.set(cle, {
        fournitureId: fournitureRef.id || `fourniture-${Date.now()}`,
        nom: nomFourniture,
        type: fournitureRef.type || 'fermeture',
        quantite,
        unite,
        prixUnitaire: prix,
        montantTotal: quantite * prix,
        descriptionDetaille: longueurCalculee !== longueurFourniture 
          ? `Calculée: ${longueurCalculee} cm, Fourniture: ${longueurFourniture} cm`
          : `Longueur: ${longueurFourniture} cm`,
        devisOrigines: [{
          devisId: devis.id || '',
          reference: devis.reference,
          quantiteUtilisee: quantiteOriginale
        }]
      });
    }
  }

  /**
   * Ajoute une fourniture au regroupement
   */
  private ajouterFourniture(
    fournituresMap: Map<string, FournitureGroupee>,
    fournituresRefMap: Map<string, Fourniture>,
    nomFourniture: string,
    quantite: number,
    unite: string,
    prixDefaut: number,
    devis: Devis,
    quantiteOriginale: number
  ) {
    const fournitureRef = fournituresRefMap.get(nomFourniture.toLowerCase());
    const prix = fournitureRef?.prix || prixDefaut;
    
    if (fournituresMap.has(nomFourniture)) {
      // Ajouter à la fourniture existante
      const fournitureGroupee = fournituresMap.get(nomFourniture)!;
      fournitureGroupee.quantite += quantite;
      fournitureGroupee.montantTotal = fournitureGroupee.quantite * fournitureGroupee.prixUnitaire;
      fournitureGroupee.devisOrigines.push({
        devisId: devis.id || '',
        reference: devis.reference,
        quantiteUtilisee: quantiteOriginale
      });
    } else {
      // Créer une nouvelle entrée
      fournituresMap.set(nomFourniture, {
        fournitureId: fournitureRef?.id || `fourniture-${Date.now()}`,
        nom: nomFourniture,
        type: fournitureRef?.type || 'accessoire',
        quantite,
        unite,
        prixUnitaire: prix,
        montantTotal: quantite * prix,
        devisOrigines: [{
          devisId: devis.id || '',
          reference: devis.reference,
          quantiteUtilisee: quantiteOriginale
        }]
      });
    }
  }

  /**
   * Ajoute une fourniture mousse avec dimensions au regroupement
   */
  private ajouterFournitureMousse(
    fournituresMap: Map<string, FournitureGroupee>,
    fournituresRefMap: Map<string, Fourniture>,
    nomFourniture: string,
    quantite: number,
    unite: string,
    prixDefaut: number,
    devis: Devis,
    quantiteOriginale: number,
    dimensions: {
      longueur: number;
      largeur: number;
      hauteur: number;
      volume: number;
    },
    dimensionsStr: string
  ) {
    const fournitureRef = fournituresRefMap.get(nomFourniture.toLowerCase());
    const prix = fournitureRef?.prix || prixDefaut;
    
    if (fournituresMap.has(nomFourniture)) {
      // Ajouter à la fourniture existante
      const fournitureGroupee = fournituresMap.get(nomFourniture)!;
      fournitureGroupee.quantite += quantite;
      fournitureGroupee.montantTotal = fournitureGroupee.quantite * fournitureGroupee.prixUnitaire;
      fournitureGroupee.devisOrigines.push({
        devisId: devis.id || '',
        reference: devis.reference,
        quantiteUtilisee: quantiteOriginale
      });
    } else {
      // Créer une nouvelle entrée avec dimensions
      fournituresMap.set(nomFourniture, {
        fournitureId: fournitureRef?.id || `fourniture-${Date.now()}`,
        nom: nomFourniture,
        type: fournitureRef?.type || 'mousse',
        quantite,
        unite,
        prixUnitaire: prix,
        montantTotal: quantite * prix,
        dimensions,
        descriptionDetaille: `${dimensionsStr} (${quantiteOriginale.toFixed(3)} m³)`,
        devisOrigines: [{
          devisId: devis.id || '',
          reference: devis.reference,
          quantiteUtilisee: quantiteOriginale
        }]
      });
    }
  }

  /**
   * Retourne le prix par défaut selon le type de mousse
   */
  private getPrixMousse(typeMousse: string): number {
    switch (typeMousse.toLowerCase()) {
      case 'souple':
        return 120;
      case 'ferme':
        return 150;
      case 'très ferme':
      case 'tres ferme':
        return 180;
      default:
        return 135; // Prix moyen
    }
  }

  /**
   * Génère une commande groupée à partir d'une liste de devis
   */
  async genererCommandeGroupee(devisList: Devis[]): Promise<CommandeGroupee> {
    try {
      // Extraire et regrouper les matières et fournitures
      const [resultMatiere, fournitures] = await Promise.all([
        this.extraireMatieresGroupees(devisList),
        this.extraireFournituresGroupees(devisList)
      ]);

      const matieres = resultMatiere.matieres;
      const statsMatiere = resultMatiere.statistiques;

      // Calculer les totaux
      const montantMatieres = matieres.reduce((total, matiere) => total + matiere.montantTotal, 0);
      const montantFournitures = fournitures.reduce((total, fourniture) => total + fourniture.montantTotal, 0);
      const montantTotal = montantMatieres + montantFournitures;

      const commandeGroupee: CommandeGroupee = {
        numeroCommande: this.genererNumeroCommande(),
        dateCreation: new Date().toISOString(),
        devisInclus: devisList.map(devis => devis.reference),
        matieres,
        fournitures,
        totaux: {
          montantMatieres: Math.round(montantMatieres * 100) / 100,
          montantFournitures: Math.round(montantFournitures * 100) / 100,
          montantTotal: Math.round(montantTotal * 100) / 100,
          nombreDevis: devisList.length
        },
        statistiques: {
          matieresExcluesStock: statsMatiere.excluesStock,
          matieresACommander: statsMatiere.aCommander
        }
      };

      return commandeGroupee;
    } catch (error) {
      console.error('Erreur lors de la génération de la commande groupée:', error);
      throw error;
    }
  }

  /**
   * Formate une commande groupée pour l'export
   */
  formaterPourExport(commande: CommandeGroupee): string {
    let contenu = `COMMANDE FOURNISSEUR - ${commande.numeroCommande}\n`;
    contenu += `Date: ${new Date(commande.dateCreation).toLocaleDateString('fr-FR')}\n`;
    contenu += `Nombre de devis: ${commande.totaux.nombreDevis}\n`;
    contenu += `Matières exclues (en stock): ${commande.statistiques.matieresExcluesStock}\n\n`;

    // Matières
    if (commande.matieres.length > 0) {
      contenu += `MATIÈRES À COMMANDER\n`;
      contenu += `${'='.repeat(50)}\n`;
      commande.matieres.forEach(matiere => {
        contenu += `${matiere.nom}\n`;
        contenu += `  Quantité: ${matiere.quantiteMetres.toFixed(2)} ml\n`;
        contenu += `  Prix unitaire: ${matiere.prixUnitaire.toFixed(2)} €/ml\n`;
        contenu += `  Montant: ${matiere.montantTotal.toFixed(2)} €\n`;
        contenu += `  Devis: ${matiere.devisOrigines.map(d => d.reference).join(', ')}\n\n`;
      });
      contenu += `Sous-total matières: ${commande.totaux.montantMatieres.toFixed(2)} €\n\n`;
    }

    // Fournitures
    if (commande.fournitures.length > 0) {
      contenu += `FOURNITURES\n`;
      contenu += `${'='.repeat(50)}\n`;
      commande.fournitures.forEach(fourniture => {
        contenu += `${fourniture.nom}\n`;
        if (fourniture.descriptionDetaille) {
          contenu += `  Dimensions: ${fourniture.descriptionDetaille}\n`;
        }
        contenu += `  Quantité: ${fourniture.quantite.toFixed(2)} ${fourniture.unite}\n`;
        contenu += `  Prix unitaire: ${fourniture.prixUnitaire.toFixed(2)} €/${fourniture.unite}\n`;
        contenu += `  Montant: ${fourniture.montantTotal.toFixed(2)} €\n`;
        contenu += `  Devis: ${fourniture.devisOrigines.map(d => d.reference).join(', ')}\n\n`;
      });
      contenu += `Sous-total fournitures: ${commande.totaux.montantFournitures.toFixed(2)} €\n\n`;
    }

    contenu += `TOTAL GÉNÉRAL: ${commande.totaux.montantTotal.toFixed(2)} €\n`;
    
    return contenu;
  }
}

// Exporter une instance unique du service
export const commandeFournisseurService = new CommandeFournisseurService();
